import React from "react";
import Facts from "components/Facts";

import { animations, previousTimeStamp, frameProcs } from "../animation";
import { addRoundedRect, borderRadius } from "./svgUtils";

// CPU and memory used to be painted the moment a reading arrived — about once a
// second — so every bar and every figure stepped. The drawn values ease towards
// the reported ones instead, spreading that step across the gap between
// messages.
//
// A quarter of the remaining distance per frame settles inside about a fifth of
// a second: close enough that the reading is never meaningfully behind, long
// enough that the step is gone. Thread count is left alone, because easing a
// count through fractional values reads as a fault rather than as motion.
const EASE_PER_FRAME = 0.25;

// Processes drop out of the set once they have arrived, so the per-frame cost is
// proportional to what is actually moving rather than to the size of the estate.
const easingProcesses = new Set();

function settled(shown, target) {
  return Math.abs(target - shown) <= Math.abs(target) * 1e-3 + 1e-4;
}

function ease(shown, target) {
  return settled(shown, target)
    ? target
    : shown + (target - shown) * EASE_PER_FRAME;
}

frameProcs.easeResources = () => {
  for (const o of easingProcesses) {
    if (!o.elem || !o.elem.isConnected) {
      easingProcesses.delete(o);
      continue;
    }

    o.shownUser = ease(o.shownUser, o.cpuUser);
    o.shownKernel = ease(o.shownKernel, o.cpuKernel);
    o.shownMem = ease(o.shownMem, o.memBytes);

    paintResources(o);

    if (
      settled(o.shownUser, o.cpuUser) &&
      settled(o.shownKernel, o.cpuKernel) &&
      settled(o.shownMem, o.memBytes)
    )
      easingProcesses.delete(o);
  }
};

function formatMb(bytes) {
  const mb = bytes / (1024 * 1024);
  // Fixed decimal places per magnitude, so the glyph count only changes when
  // the magnitude does. toLocaleString trimmed trailing zeros, which flapped
  // the text between six and seven glyphs as an eased value swept past them -
  // and every flap was a full batch rebuild.
  if (mb < 10) return mb.toFixed(3) + " MB";
  if (mb < 100) return mb.toFixed(2) + " MB";
  if (mb < 1000) return mb.toFixed(1) + " MB";
  return Math.round(mb).toLocaleString() + " MB";
}

// A bar width that never reaches zero and moves in quarter units. The floor
// matters to the renderer: a zero-size rect emits no vertices, so a bar
// crossing zero changed the tile's vertex count and forced a full batch
// rebuild - and idle kernel bars cross zero constantly. A hundredth of a unit
// is far below a pixel. The quantisation keeps the written value repeating
// while a bar eases, so most frames the write no-ops.
function barWidth(w) {
  return Math.max(0.01, Math.round(w * 4) / 4);
}

// Draws whatever the eased values currently are. Split out of the message
// handler so the frame loop can call it too.
function paintResources(o) {
  const texts = o.elem.selectAll("g>text");
  if (texts == null || texts.length < 2) return;

  const kernelPerc = o.shownKernel;
  const totalPerc = o.shownUser + o.shownKernel;

  // Off the reported core count rather than the eased one, so easing across a
  // core boundary cannot add and remove a bar on alternate frames.
  const coreNeed = Math.max(Math.ceil(o.cpuCores), 1);

  // Bars are added the moment the load needs them, but only removed once the
  // need has been gone for a while: load hovering around a whole core
  // otherwise added and removed a bar - a structure change and a full batch
  // rebuild - on alternate readings.
  let coreReq = o._coresShown || coreNeed;
  if (coreNeed >= coreReq) {
    coreReq = coreNeed;
    o._coresBelowSince = undefined;
  } else {
    const now = performance.now();
    if (o._coresBelowSince === undefined) o._coresBelowSince = now;
    else if (now - o._coresBelowSince > 10000) {
      coreReq = coreNeed;
      o._coresBelowSince = undefined;
    }
  }
  o._coresShown = coreReq;

  const rects = o.adjustRectsForCores(coreReq);
  if (rects == null || rects.length < 3) return;

  // Only when the rendered string changes: at 60fps this would otherwise be a
  // text write per process per frame, which is exactly the per-element cost the
  // renderer exists to avoid.
  const memText = formatMb(o.shownMem);
  if (o._memText !== memText) {
    o._memText = memText;
    texts[0].textContent = memText;
  }

  const threadsText = o.threads + " threads";
  if (o._threadsText !== threadsText) {
    o._threadsText = threadsText;
    texts[1].textContent = threadsText;
  }

  for (let i = 0; i < coreReq; i++) {
    const green = rects[i * 3 + 1];
    const red = rects[i * 3 + 2];

    const max = i + 1;

    if (kernelPerc < i) {
      // red ends before this core
      red.attr("width", barWidth(0));

      if (totalPerc < i) {
        // green ends before this one too
        green.attr("width", barWidth(0));
      } else if (totalPerc >= max) {
        // green covers this whole one
        green.attr("width", barWidth(o.barWidth));
      } else {
        // green ends within this one
        green.attr("x", barStart + (o.barWidth + barGap) * i);
        green.attr("width", barWidth((totalPerc - i) * o.barWidth));
      }
    } else if (kernelPerc >= max) {
      // red ends after this one
      red.attr("width", barWidth(o.barWidth));
      green.attr("width", barWidth(0));
    } else {
      // red ends within this one
      red.attr("width", barWidth((kernelPerc - i) * o.barWidth));

      if (totalPerc >= max) {
        // green covers this whole one
        green.attr("width", barWidth(o.barWidth));
      } else {
        // green ends within this one
        green.attr("width", barWidth((totalPerc - i) * o.barWidth));
      }
    }
  }
}

//const lastUsageCache = {};
//const currUsageCache = {};

const width = 76;
const barAvailable = width - 2;
const barGap = 6;
const barStart = -38 + 1;

export default {
  hasData: true,
  hasRag: true,
  collections: { roles: { objectType: "role" } },

  layout: { radius: 43 },
  render: {
    enter: (item) => {
      const { elem: e, id } = item;

      var g = e.append("g");

      addRoundedRect(g, -38, -22, width, 44, borderRadius, 0.5)
        .attr("fill", "white")
        .attr("stroke", "black");

      g.append("text")
        .attr("x", -38 + 2)
        .attr("y", -22 + 12 + 2)
        .text("");
      g.append("text")
        .attr("x", -38 + 2)
        .attr("y", 22 - 2)
        .text("");

      item.adjustRectsForCores = (cores) => {
        let rects = g.selectAll("g>rect");
        let current = rects.length / 3;

        if (current == cores) return rects;

        while (current < cores) {
          g.append("rect")
            .attr("y", -6)
            .attr("height", 12)
            .attr("fill", "#f0f0f0");

          g.append("rect")
            .attr("y", -6)
            .attr("height", 12)
            .attr("fill", "#50e050");

          g.append("rect")
            .attr("y", -6)
            .attr("height", 12)
            .attr("fill", "#e05050");

          current++;
        }

        while (current > cores) {
          rects[rects.length - 1].remove();
          rects[rects.length - 2].remove();
          rects[rects.length - 3].remove();

          current--;
        }

        rects = g.selectAll("g>rect");

        const barAndGap = (barAvailable + barGap) / cores;
        item.barWidth = barAndGap - barGap;

        let x = barStart;
        for (let i = 0; i < cores; i++) {
          const bg = rects[3 * i];
          const green = rects[3 * i + 1];
          const red = rects[3 * i + 2];

          bg.attr("x", x).attr("width", item.barWidth);
          // The same floor as barWidth(), so the first paint after a build
          // cannot change the vertex count.
          green.attr("x", x).attr("width", 0.01);
          red.attr("x", x).attr("width", 0.01);

          x += barAndGap;
        }

        return rects;
      };

      // currUsageCache[id] = {
      //   user: 0.5,
      //   kernel: 0.5,
      // };

      return e;
    },
  },

  responseHandlers: {
    threadinfo: (o, n, ui) => ui.setThreadData(JSON.parse(n)),
    memoryinfo: (o, n, ui) => ui.setMemoryData(JSON.parse(n)),
    channelready: (o, data, ui, path, send) => {
      const info = data.split(" ");
      const hostname = info[0];
      const port = parseInt(info[1]);
      const type = info[2];
      switch (type) {
        case "ssh":
          ui.tunnelModal({
            title: "SSH tunnel",
            comment:
              "Your SSH session is ready. You can use the below command to connect.",
            command: "ssh root@" + hostname + " -p " + port,
            handleClose: () => {
              send("stop-ssh " + path);
            },
          });
          break;
        default:
          alert(
            "Unknown channel type: " +
              type +
              ".\nReady at " +
              hostname +
              ":" +
              port
          );
          break;
      }
    },
    channelstats: (o, data, ui) => {
      const stats = JSON.parse(data);
      ui.tunnelStats(stats);
    },
  },

  eventHandlers: {
    resources: (o, n) => {
      const texts = o.elem.selectAll("g>text");
      if (texts == null || texts.length < 2) return;

      const data = n.split(",");
      const userPerc = parseFloat(data[0]);
      const kernelPerc = parseFloat(data[1]);

      o.cpuUser = userPerc;
      o.cpuKernel = kernelPerc;
      o.cpuCores = userPerc + kernelPerc;
      o.memBytes = parseInt(data[3]);
      o.threads = parseInt(data[2]);

      // The first reading has nothing to ease from, so it is drawn as reported;
      // easing up from zero would have every process fill its bars on arrival.
      if (o.shownUser === undefined) {
        o.shownUser = o.cpuUser;
        o.shownKernel = o.cpuKernel;
        o.shownMem = o.memBytes;
        paintResources(o);
        return;
      }

      easingProcesses.add(o);
    },

    ping: (o, n) => {
      return; // dont bother.

      // ping!
      var grp = o.elem.select("g");
      if (grp == null) return;
      var pe = grp.parentElement;

      /*
      const dot = pe.addFirstChild("line")
        .attr("stroke-width", "5")
        .attr("stroke", "#999");
      animations.add({
        start: previousTimeStamp,
        duration: 60000,
        from: 0,
        to: 1,
        frame: (v, done) => {
          const outline = o.elem.select("path");
          const totalLen = outline.getTotalLength();
          const p1 = outline.getPointAtLength(totalLen * v);
          const p2 = outline.getPointAtLength(totalLen * (v + 0.02));
          dot.attr("x1", p1.x * 1.1)
            .attr("y1", p1.y * 1.1)
            .attr("x2", p2.x * 1.1)
            .attr("y2", p2.y * 1.1);
        }
      });*/
      animations.add({
        type: "animate-ping-scale",
        start: previousTimeStamp,
        duration: 590,
        from: 255,
        to: 0,
        frame: (v) => {
          grp.setAttribute(
            "transform",
            "scale(" + (1 + v / 350) + "," + (1 + v / 350) + ")"
          );
        },
      });
      var c = pe.append("circle");
      let col;
      if (n < 10) col = "#50ff50";
      else if (n < 50) col = "#7fff50";
      else if (n < 200) col = "#ff7f50";
      else col = "#ff5050";
      c.attr("stroke", col)
        .attr("stroke-width", "10")
        .attr("fill", "none")
        .attr("cx", "0")
        .attr("cy", "0")
        .attr("r", "0");
      animations.add({
        type: "animate-ping-radius-opacity",
        start: previousTimeStamp,
        duration: 1000,
        from: 0,
        to: 500,
        frame: (v, done) => {
          c.attr("r", v / 3).attr("opacity", 0.5 - v / 1000);
          if (done) {
            c.remove();
          }
        },
      });
    },
  },

  renderInfoHead: (t) => "Process " + t.label,
  renderInfoBody: (t) => {
    if (!t.data) {
      return <p>No data yet</p>;
    }
    const {
      connection,
      commandLine,
      saffronVersion,
      appVersion,
      builtUtc,
      processStartUtc,
      runningAs,
      internalIp,
      _annotations,
    } = JSON.parse(t.data);
    const processStartLocal = processStartUtc
      ? new Date(processStartUtc).toLocaleString()
      : null;
    const builtLocal = builtUtc ? new Date(builtUtc).toLocaleString() : null;
    return (
      <React.Fragment>
        <Facts>
          <Facts.Fact
            label="App build"
            value={
              appVersion
                ? appVersion + " (" + builtLocal + ")"
                : builtUtc
                ? builtLocal
                : "Unknown"
            }
          />
          <Facts.Fact
            label="Saffron version"
            value={saffronVersion || "Unknown"}
          />
          <Facts.Fact
            label="Started"
            value={processStartUtc ? processStartLocal : "Unknown"}
          />
          <Facts.Fact label="Internal IP" value={internalIp || "Unknown"} />
          <Facts.Fact
            label="Borg connection ID"
            value={connection || "Unknown"}
          />
          <Facts.Fact label="Command line" value={commandLine || "Unknown"} />
          <Facts.Fact label="Running as" value={runningAs || "Unknown"} />
        </Facts>
        {_annotations && _annotations.length > 0 && (
          <div>
            <h4>External resource annotations</h4>
            <table>
              <thead style={{ fontWeight: "bold" }}>
                <tr>
                  <td style={{ minWidth: "10%" }}>Type</td>
                  <td style={{ minWidth: "10%" }}>Name</td>
                  <td>Data</td>
                </tr>
              </thead>
              <tbody style={{ fontFamily: "courier" }}>
                {_annotations.map((a, i) => (
                  <tr key={i}>
                    <td>{a.type}</td>
                    <td>{a.name}</td>
                    <td>{a.data}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </React.Fragment>
    );
  },
};
